import { erc20Abi, getContract, type Address, type WalletClient } from "viem";
import { getLockAddress, LOCK_ADDRESSES, LOCK_CONTROLLER_ADDRESSES, publicClients } from "@/config";
import { truncateDecimals, formatRawTokenAmount } from "@/utils";
import { LOCK_TOKEN_DECIMALS } from "@/config";
import { mainnet } from "viem/chains";
import { lockingControllerAbi } from "@/abi/lockingController.abi";

export interface LockedBalancesResponse {
    lockDuration: string;
    liUSDBalance: string;
    lockedBalance: string;
    iUSDBalance: string;
    rewardsEarned: string;
}

export type TransactionRequestMinimal = {
    to: `0x${string}`;
    data: `0x${string}`;
};

export const fetchLockedBalances = async (
    userAddress: Address,
    _walletClient?: WalletClient, // @deprecated not used
): Promise<LockedBalancesResponse[]> => {
    try {
        const lockOptions = Object.keys(LOCK_ADDRESSES[mainnet.id]);

        const balanceCalls = lockOptions.map((duration) => {
            const lockAddress = getLockAddress(mainnet.id, Number(duration.replace("w", "")));
            return getContract({
                address: lockAddress,
                abi: erc20Abi,
                client: publicClients[mainnet.id],
            });
        });

        const balancesResult = await publicClients[mainnet.id].multicall({
            contracts: balanceCalls.map((contract) => ({
                address: contract.address,
                abi: contract.abi,
                functionName: "balanceOf",
                args: [userAddress],
            })),
            allowFailure: false,
        });

        const exchangeRateCalls = lockOptions.map((duration) => ({
            contract: getContract({
                address: LOCK_CONTROLLER_ADDRESSES[mainnet.id],
                abi: lockingControllerAbi,
                client: publicClients[mainnet.id],
            }),
            duration,
        }));

        const exchangeRatesResult = await publicClients[mainnet.id].multicall({
            contracts: exchangeRateCalls.map(({ contract, duration }) => ({
                address: contract.address,
                abi: contract.abi,
                functionName: "exchangeRate",
                args: [Number(duration.replace("w", ""))],
            })),
            allowFailure: false,
        });

        return lockOptions.map((duration, index) => {
            try {
                const result = balancesResult[index];

                // Get the exchange rate for this duration
                const exchangeRate = exchangeRatesResult[index];
                // Calculate converted balance and rewards
                const originalBalance = BigInt(result);
                const exchangeRateValue = BigInt(exchangeRate);

                // Format balances to proper decimals
                const convertedBalance = (originalBalance * exchangeRateValue) / 10n ** BigInt(LOCK_TOKEN_DECIMALS);
                const properDecimalConvertedBalance = formatRawTokenAmount(convertedBalance, LOCK_TOKEN_DECIMALS);

                const rewardBalance = convertedBalance - originalBalance;
                const properDecimalRewardBalance = formatRawTokenAmount(rewardBalance, LOCK_TOKEN_DECIMALS);

                return {
                    lockDuration: duration,
                    liUSDBalance: truncateDecimals(originalBalance, 18),
                    lockedBalance: truncateDecimals(properDecimalConvertedBalance),
                    iUSDBalance: truncateDecimals(convertedBalance, 18),
                    rewardsEarned: truncateDecimals(properDecimalRewardBalance),
                };
            } catch (error) {
                console.error(`Error processing balance for duration ${duration}:`, error);
                return {
                    lockDuration: duration,
                    liUSDBalance: truncateDecimals(0n),
                    iUSDBalance: truncateDecimals(0n),
                    lockedBalance: truncateDecimals(0n),
                    rewardsEarned: truncateDecimals(0n),
                };
            }
        });
    } catch (error) {
        console.error("Error fetching all locked balances:", error);
        throw error;
    }
};
